import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import Backdrop from './Backdrop/Backdrop';

const readViewport = () => {
  if (typeof window === 'undefined') return { height: '100dvh', top: '0px' };
  const viewport = window.visualViewport;
  return {
    height: `${Math.round(viewport?.height || window.innerHeight)}px`,
    top: `${Math.round(viewport?.offsetTop || 0)}px`,
  };
};

const Modal = ({ opened, close, children }) => {
  const [viewport, setViewport] = useState(readViewport);

  useEffect(() => {
    if (!opened) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    const updateViewport = () => setViewport(readViewport());
    updateViewport();
    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);

    const immersiveEvent = new CustomEvent('lpb:immersive-state', { detail: { open: true, source: 'modal' } });
    window.dispatchEvent(immersiveEvent);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') close?.();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
      document.removeEventListener('keydown', handleKeyDown);
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
      window.dispatchEvent(new CustomEvent('lpb:immersive-state', { detail: { open: false, source: 'modal' } }));
    };
  }, [opened, close]);

  const portal = document.getElementById('root-modal');
  const modalStyle = useMemo(() => ({
    '--lpb-modal-height': viewport.height,
    '--lpb-modal-top': viewport.top,
  }), [viewport]);

  if (!portal) return null;

  return ReactDOM.createPortal(
    <>
      <Backdrop close={close} opened={opened} />
      <WrappedModal
        opened={opened}
        role="dialog"
        aria-modal="true"
        aria-hidden={!opened}
        style={modalStyle}
      >
        <InsideWrapper>{children}</InsideWrapper>
      </WrappedModal>
    </>,
    portal,
  );
};

const WrappedModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 2010;
  width: min(calc(100% - 2rem), 42rem);
  max-height: min(90vh, 52rem);
  max-height: min(90dvh, 52rem);
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scroll-padding-bottom: 7rem;
  border: 1px solid rgba(17, 17, 17, 0.1);
  border-radius: 1.4rem;
  background: #fff;
  box-shadow: 0 2rem 5rem rgba(0, 0, 0, 0.28);
  opacity: ${({ opened }) => (opened ? 1 : 0)};
  visibility: ${({ opened }) => (opened ? 'visible' : 'hidden')};
  transform: ${({ opened }) => opened
    ? 'translate(-50%, -50%) scale(1)'
    : 'translate(-50%, -46%) scale(0.97)'};
  transition: opacity 150ms ease, transform 150ms ease, visibility 150ms ease;

  @media (max-width: 640px) {
    top: var(--lpb-modal-top, 0px);
    left: 0;
    width: 100%;
    height: var(--lpb-modal-height, 100dvh);
    max-height: none;
    border: 0;
    border-radius: 0;
    transform: ${({ opened }) => opened ? 'translateY(0)' : 'translateY(1rem)'};
  }
`;

const InsideWrapper = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: clamp(1.35rem, 5vw, 2.5rem);

  @media (max-width: 640px) {
    min-height: 100%;
    padding:
      max(1rem, env(safe-area-inset-top))
      max(1rem, env(safe-area-inset-right))
      calc(1.25rem + env(safe-area-inset-bottom))
      max(1rem, env(safe-area-inset-left));
  }
`;

export default Modal;
